import React from "react";

// Shared drawing kit for the Working Man scenes.
//
// Every scene renders into one <svg viewBox="0 0 1600 900">. Scenes are pure
// functions of time, so nothing here (or in a scene) may call Math.random() or
// Date.now() while rendering: use rng(seed) at module scope instead.

export const VB = { w: 1600, h: 900 };
// Wide screens crop the edges; keep anything that matters inside SAFE.
// The band below SAFE.y1 sits under the timeline.
export const SAFE = { x0: 160, y0: 90, x1: 1440, y1: 760 };
// Paint full-bleed backgrounds over this rect so tall screens stay filled.
export const BLEED = { x: -400, y: -900, w: 2400, h: 2700 };

export const FONT = {
  mono: '"JetBrains Mono", ui-monospace, Menlo, monospace',
  serif: '"Newsreader", "Times New Roman", serif',
};

export const C = {
  ink: ["#000000", "#0d0d0d", "#1a1a1a", "#2b2b2b", "#4a4a4a", "#7a7a7a", "#b8b8b8", "#e8e8e8"],
  accent: "#537fa4",
  // People: sodium lamps, dawn, kitchen bulbs.
  warm: "#e9a25b",
  warmHot: "#ffd29a",
  warmDeep: "#7a3f17",
  // Machines: server LEDs, data center glow.
  cold: "#a8c8e6",
  coldHot: "#eaf5ff",
  coldDeep: "#1b2c3d",
  night: "#05070b",
};

export const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
export const lerp = (a, b, k) => a + (b - a) * k;
// Remaps p from [a, b] to [0, 1], clamped: seg(p, 0.2, 0.5) is 0 before 0.2 and 1 after 0.5.
export const seg = (p, a, b) => clamp((p - a) / (b - a));
// Rises over [a, b], holds at 1, falls over [c, d].
export const span = (p, a, b, c, d) => Math.min(seg(p, a, b), 1 - seg(p, c, d));
export const smooth = (k) => k * k * (3 - 2 * k);
export const ease = {
  in: (k) => k * k * k,
  out: (k) => 1 - Math.pow(1 - k, 3),
  inOut: (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2),
};

export const mixColor = (a, b, k) => {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (shift) => Math.round(lerp((pa >> shift) & 255, (pb >> shift) & 255, clamp(k)));
  return "#" + ((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1);
};

// Deterministic PRNG (mulberry32): const r = rng(7); r() -> [0, 1).
export const rng = (seed = 1) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let x = a;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
};

// Hanging cable between two points; sag is the drop at the midpoint.
export const wire = (x1, y1, x2, y2, sag = 30) =>
  `M ${x1} ${y1} Q ${(x1 + x2) / 2} ${(y1 + y2) / 2 + sag * 2} ${x2} ${y2}`;

const deg = (rad) => (rad * 180) / Math.PI;

// A stroke that draws itself in as p goes 0 -> 1.
export function Draw({ d, p = 1, ...rest }) {
  if (p <= 0) return null;
  return (
    <path
      d={d}
      pathLength={1}
      strokeDasharray="1 1"
      strokeDashoffset={1 - clamp(p)}
      fill="none"
      {...rest}
    />
  );
}

// Uppercase monospace label, the documentary-chyron voice of the piece.
export function Label({
  x, y, children, size = 14, color = C.ink[5], anchor = "start",
  spacing = 0.18, weight = 400, opacity = 1,
}) {
  const text = typeof children === "string" ? children.toUpperCase() : children;
  return (
    <text
      x={x} y={y} fill={color} opacity={opacity}
      fontFamily={FONT.mono} fontSize={size} fontWeight={weight}
      letterSpacing={`${spacing}em`} textAnchor={anchor}
    >
      {text}
    </text>
  );
}

// ---------------------------------------------------------------------------
// The worker: one silhouette used by every act so he reads as the same man.
//
// Origin is between his feet on the ground; he is ~170 units tall at s=1 and
// faces right (facing=-1 mirrors him). Poses: stand, walk, work, slump,
// lookUp, sit. `phase` (radians) drives walk and work cycles, e.g. t * 6.
// Sitting puts his seat ~45 units above the ground, so draw a stool there.
// Working rests his far hand ~95 units up and ~50 in front of him (put the
// bench top there) while the near hand swings like a hammer; hammer={0..1}
// fades one into that hand. brim="short" trims the cap's peak to a wedge for
// close shots, where the long one reads as a stick across his face.
// `pose` also takes an angle set, so changes of
// pose can blend instead of cutting: pose={mixPose("slump", "lookUp", k)}.
// workerJoints(props) returns { hand, farHand, head } in scene coordinates
// for attaching tools, a mug, a cigarette glow...
// ---------------------------------------------------------------------------

const LEG = [42, 40];
const ARM = [27, 25];
const limb = (x, y, a, len) => [x + len * Math.sin(a), y + len * Math.cos(a)];

// Absolute joint angles in radians from straight down; positive swings forward.
// Index 0 is the far limb, 1 the near limb.
export function workerPose(pose = "stand", phase = 0) {
  const s = Math.sin(phase);
  switch (pose) {
    case "walk": {
      const leg = (ph) => {
        const th = 0.42 * Math.sin(ph);
        return [th, th - 0.75 * Math.max(0, Math.cos(ph))];
      };
      return {
        lean: 0.06, head: 0.02,
        legs: [leg(phase + Math.PI), leg(phase)],
        arms: [[0.34 * s, 0.34 * s + 0.25], [-0.34 * s, -0.34 * s + 0.25]],
      };
    }
    case "work":
      return {
        lean: 0.24, head: 0.32,
        legs: [[-0.1, -0.1], [0.12, 0.12]],
        arms: [[0.7, 1.3], [0.75 + 0.35 * s, 2.025 + 0.575 * s]],
      };
    case "slump":
      return {
        lean: 0.3, head: 0.6,
        legs: [[0.02, -0.04], [0.1, 0.03]],
        arms: [[0.12, 0.08], [0.18, 0.12]],
      };
    case "lookUp":
      return {
        lean: -0.08, head: -0.55,
        legs: [[-0.06, -0.06], [0.07, 0.07]],
        arms: [[-0.1, -0.05], [0.06, 0.12]],
      };
    case "sit":
      return {
        lean: 0.14, head: 0.42,
        legs: [[1.45, 0.05], [1.6, 0.12]],
        arms: [[0.75, 1.5], [0.9, 1.62]],
      };
    default:
      return {
        lean: 0.03, head: 0,
        legs: [[-0.05, -0.05], [0.06, 0.06]],
        arms: [[-0.08, -0.02], [0.1, 0.18]],
      };
  }
}

const poseAngles = (pose, phase) => (typeof pose === "string" ? workerPose(pose, phase) : pose);

// Blends two poses (names or angle sets) by k; phase drives either cycle.
export function mixPose(a, b, k, phase = 0) {
  const A = poseAngles(a, phase);
  const B = poseAngles(b, phase);
  const pair = (la, lb) => la.map(([x, y], i) => [lerp(x, lb[i][0], k), lerp(y, lb[i][1], k)]);
  return {
    lean: lerp(A.lean, B.lean, k),
    head: lerp(A.head, B.head, k),
    legs: pair(A.legs, B.legs),
    arms: pair(A.arms, B.arms),
  };
}

function rig(pose, phase) {
  const P = poseAngles(pose, phase);
  const hipY = -Math.max(...P.legs.map(([a, b]) => LEG[0] * Math.cos(a) + LEG[1] * Math.cos(b)));
  const cl = Math.cos(P.lean);
  const sl = Math.sin(P.lean);
  const torso = (px, py) => [px * cl - py * sl, hipY + px * sl + py * cl];
  const sh = torso(0, -47);
  const ha = P.lean + P.head;
  const head = [sh[0] + 17 * Math.sin(ha), sh[1] - 17 * Math.cos(ha)];
  const arm = ([a, b], dx) => {
    const o = [sh[0] + dx, sh[1] + 4];
    const e = limb(o[0], o[1], a, ARM[0]);
    const h = limb(e[0], e[1], b, ARM[1]);
    return { d: `M ${o[0]} ${o[1]} L ${e[0]} ${e[1]} L ${h[0]} ${h[1]}`, hand: h, b };
  };
  return { P, hipY, torso, sh, ha, head, far: arm(P.arms[0], -2), near: arm(P.arms[1], 3) };
}

export function workerJoints({ x = 0, y = 0, s = 1, facing = 1, pose = "stand", phase = 0 }) {
  const R = rig(pose, phase);
  const world = ([px, py]) => [x + px * s * facing, y + py * s];
  return { hand: world(R.near.hand), farHand: world(R.far.hand), head: world(R.head) };
}

export function Worker({
  x = 0, y = 0, s = 1, color = C.ink[0], facing = 1, pose = "stand",
  phase = 0, hat = "cap", brim = "full", pail = false, hammer = 0, opacity = 1,
}) {
  const { P, hipY, torso, sh, ha, head, far, near } = rig(pose, phase);
  const legD = ([a, b], dx) => {
    const k = limb(dx, hipY, a, LEG[0]);
    const f = limb(k[0], k[1], b, LEG[1]);
    return `M ${dx} ${hipY} L ${k[0]} ${k[1]} L ${f[0]} ${f[1]} L ${f[0] + 9} ${f[1]}`;
  };
  const body = [torso(-9, 2), torso(9, 2), torso(13, -47), torso(-12, -47)]
    .map(([px, py], i) => `${i ? "L" : "M"} ${px} ${py}`)
    .join(" ") + " Z";
  const [hx, hy] = near.hand;
  // The hammer rides the near hand, handle tipped a little above the forearm.
  const grip = near.b + 0.35;
  const [ex, ey] = limb(hx, hy, grip, 17);
  const [nx, ny] = [Math.sin(grip - Math.PI / 2), Math.cos(grip - Math.PI / 2)];

  return (
    <g
      transform={`translate(${x} ${y}) scale(${s * facing} ${s})`}
      opacity={opacity}
      stroke={color}
      fill={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={legD(P.legs[0], -3)} fill="none" strokeWidth={12} />
      <path d={far.d} fill="none" strokeWidth={9} />
      <path d={legD(P.legs[1], 3)} fill="none" strokeWidth={12} />
      <path d={body} strokeWidth={8} />
      <path d={`M ${sh[0]} ${sh[1]} L ${head[0]} ${head[1]}`} strokeWidth={8} />
      <circle cx={head[0]} cy={head[1]} r={11.5} stroke="none" />
      <g transform={`translate(${head[0]} ${head[1]}) rotate(${deg(ha)})`}>
        {hat === "cap" && (
          <>
            <path d="M -12.5 -2 A 12.5 12.5 0 0 1 12.5 -2 Z" stroke="none" />
            {brim === "short"
              ? <path d="M 5 -4.5 Q 12.5 -4.2 15 -1.4 L 6.5 0 Z" stroke="none" />
              : <path d="M 4 -3 L 21 0" fill="none" strokeWidth={4} />}
          </>
        )}
        {hat === "hard" && (
          <>
            <path d="M -13.5 -1 A 13.5 13.5 0 0 1 13.5 -1 Z" stroke="none" />
            <path d="M -17 -1 L 18 -1" fill="none" strokeWidth={4} />
          </>
        )}
      </g>
      <path d={near.d} fill="none" strokeWidth={9} />
      {hammer > 0 && (
        <g opacity={hammer}>
          <path d={`M ${hx} ${hy} L ${ex} ${ey}`} fill="none" strokeWidth={3.2} />
          <path d={`M ${ex - 5 * nx} ${ey - 5 * ny} L ${ex + 7 * nx} ${ey + 7 * ny}`} fill="none" strokeWidth={6} strokeLinecap="butt" />
        </g>
      )}
      {pail && (
        <g strokeWidth={2.5}>
          <path d={`M ${hx - 7} ${hy + 7} Q ${hx} ${hy - 5} ${hx + 7} ${hy + 7}`} fill="none" />
          <rect x={hx - 10} y={hy + 6} width={20} height={14} rx={2} stroke="none" />
        </g>
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// The data center: the same windowless block wherever it appears.
//
// (x, y) is its bottom-left corner on the ground. build 0 -> 1 draws the
// outline, fills the walls, then powers it up; glow 0 -> 1 is how hard it is
// running (LEDs, fans, cold light spill). Pass a unique id per instance.
// ---------------------------------------------------------------------------

const DC_LEDS = (() => {
  const r = rng(4242);
  return Array.from({ length: 36 }, () => ({
    u: r(), v: r(), rate: 0.6 + r() * 3.2, ph: r() * 6.28, hot: r() > 0.7,
  }));
})();

export function DataCenter({
  id = "dc", x = 0, y = 0, w = 520, h = 150, build = 1, glow = 1, t = 0,
  label = "SITE 7", fence = true,
}) {
  const shell = seg(build, 0, 0.55);
  const walls = seg(build, 0.45, 0.8);
  const power = glow * seg(build, 0.85, 1);
  const top = y - h;
  const strips = Math.max(4, Math.round(w / 64));
  const units = Math.max(3, Math.floor(w / 66));
  const unitW = 46;
  const gap = (w - units * unitW) / (units + 1);
  const outline = `M ${x} ${y} L ${x} ${top} L ${x + w} ${top} L ${x + w} ${y}`;

  return (
    <g>
      <defs>
        <linearGradient id={`${id}-face`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#121a23" />
          <stop offset="1" stopColor="#070a0e" />
        </linearGradient>
        <radialGradient id={`${id}-spill`}>
          <stop offset="0" stopColor={C.cold} stopOpacity="0.55" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
      </defs>

      {power > 0 && (
        <>
          <ellipse cx={x + w / 2} cy={top} rx={w * 0.75} ry={h * 0.9} fill={`url(#${id}-spill)`} opacity={power * 0.45} />
          <ellipse cx={x + w / 2} cy={y + 12} rx={w * 0.95} ry={70} fill={`url(#${id}-spill)`} opacity={power * 0.7} />
        </>
      )}

      {walls > 0 && (
        <g opacity={walls}>
          {Array.from({ length: units }, (_, i) => {
            const ux = x + gap + i * (unitW + gap);
            const cx = ux + unitW / 2;
            const cy = top - 10;
            const spin = deg(t * 9 * power) + i * 37;
            return (
              <g key={i}>
                <rect x={ux} y={top - 20} width={unitW} height={20} fill="#0b1016" stroke="#1f2c39" strokeWidth={1.5} />
                <circle cx={cx} cy={cy} r={7.5} fill="none" stroke="#2c3e50" strokeWidth={1.5} />
                <g transform={`rotate(${spin} ${cx} ${cy})`} stroke="#3e566d" strokeWidth={2} strokeLinecap="round">
                  <line x1={cx} y1={cy} x2={cx} y2={cy - 6} />
                  <line x1={cx} y1={cy} x2={cx + 5.2} y2={cy + 3} />
                  <line x1={cx} y1={cy} x2={cx - 5.2} y2={cy + 3} />
                </g>
              </g>
            );
          })}
          <rect x={x} y={top} width={w} height={h} fill={`url(#${id}-face)`} />
          {Array.from({ length: Math.floor(h / 26) }, (_, i) => (
            <line key={i} x1={x} x2={x + w} y1={top + 26 * (i + 1)} y2={top + 26 * (i + 1)} stroke="#17222d" strokeWidth={1} />
          ))}
          {label && (
            <text x={x + 18} y={top + 24} fill="#3a5068" fontFamily={FONT.mono} fontSize={12} letterSpacing="0.3em">
              {label}
            </text>
          )}
        </g>
      )}

      {power > 0 && (
        <g>
          {Array.from({ length: strips }, (_, i) => {
            const sx = x + 30 + (i * (w - 60)) / (strips - 1);
            const k = 0.6 + 0.25 * Math.sin(t * 1.3 + i * 1.7);
            return <rect key={i} x={sx - 1.5} y={top + h * 0.16} width={3} height={h * 0.7} fill={C.cold} opacity={power * k} />;
          })}
          {DC_LEDS.map((d, i) => {
            const on = Math.sin(t * d.rate + d.ph) > 0.15;
            return (
              <rect
                key={i}
                x={x + 14 + d.u * (w - 28)}
                y={top + 34 + d.v * (h - 50)}
                width={3}
                height={3}
                fill={d.hot ? C.coldHot : C.accent}
                opacity={power * (on ? 1 : 0.15)}
              />
            );
          })}
        </g>
      )}

      {shell > 0 && walls < 1 && (
        <Draw d={outline} p={shell} stroke={C.cold} strokeOpacity={0.7} strokeWidth={2} />
      )}

      {fence && walls > 0 && (
        <g stroke="#2a3642" strokeWidth={1.5} opacity={walls}>
          <line x1={x - 50} x2={x + w + 50} y1={y + 2} y2={y + 2} />
          <line x1={x - 50} x2={x + w + 50} y1={y + 14} y2={y + 14} />
          {Array.from({ length: Math.floor((w + 100) / 18) + 1 }, (_, i) => (
            <line key={i} x1={x - 50 + i * 18} x2={x - 50 + i * 18} y1={y - 6} y2={y + 26} />
          ))}
        </g>
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Transmission pylon, ~125 units tall at s=1, base centred on (x, y).
// pylonArms(x, y, s) gives the six wire attachment points, left to right,
// top crossarm first.
// ---------------------------------------------------------------------------

export const pylonArms = (x, y, s = 1) => [
  [x - 34 * s, y - 96 * s], [x, y - 104 * s], [x + 34 * s, y - 96 * s],
  [x - 28 * s, y - 72 * s], [x, y - 76 * s], [x + 28 * s, y - 72 * s],
];

export function Pylon({ x, y, s = 1, color = C.ink[3], sw = 2 }) {
  const brace = [];
  for (let i = 0; i < 5; i++) {
    const y0 = -i * 20;
    const y1 = -(i + 1) * 20;
    const w0 = 22 - i * 3.2;
    const w1 = 22 - (i + 1) * 3.2;
    brace.push(`M ${-w0} ${y0} L ${w1} ${y1} M ${w0} ${y0} L ${-w1} ${y1}`);
  }
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} stroke={color} strokeWidth={sw / s} fill="none" strokeLinecap="round">
      <path d="M -22 0 L -6 -110 L 0 -124 L 6 -110 L 22 0" />
      <path d={brace.join(" ")} strokeWidth={(sw * 0.6) / s} />
      <path d="M -34 -96 L 34 -96 M -28 -72 L 28 -72" />
      <path d="M -34 -96 L -6 -86 M 34 -96 L 6 -86 M -28 -72 L -6 -64 M 28 -72 L 6 -64" strokeWidth={(sw * 0.6) / s} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// The arm: THE machine, a generic floor-mounted industrial arm (base, three
// links, a tool tip), the same wherever it replaces him.
//
// (x, y) is the centre of its base on the floor; it faces right at s=1
// (facing=-1 mirrors it) and in its home pose reaches ~160 units up and ~95
// out. pose is three joint angles: ARM_POSES holds the stock ones (fold,
// home, a, aHit, b, bHit, scan), and armCycle(f) walks one machine cycle as
// f goes 0 -> 1 (two presses, then a scan), landing the presses at ARM_HITS.
// armTip(pose) is the tool tip in the arm's own units, for sparks. lit 0 -> 1
// runs from a dark silhouette with a cold rim to pale lit steel; led is its
// status light; hoist draws the cables it is lowered in on.
// taper(points, widths) outlines a tapered limb with a round tip: fingers.
// ---------------------------------------------------------------------------

const ARM_L1 = 105;
const ARM_L2 = 92;
const ARM_L3 = 34;
const ARM_SH = 62; // shoulder height above the floor
const round1 = (v) => (Math.round(v * 10) / 10).toString();
const linePath = (list) => list.map(([px, py], i) => `${i ? "L" : "M"} ${round1(px)} ${round1(py)}`).join(" ");

// Tool tip (tx, ty) at tool angle c (from straight up) -> joint angles.
function armIk(tx, ty, c) {
  const wx = tx - ARM_L3 * Math.sin(c);
  const wy = ty + ARM_L3 * Math.cos(c);
  const dy = wy + ARM_SH;
  const d = Math.min(ARM_L1 + ARM_L2 - 1, Math.hypot(wx, dy));
  const phi = Math.atan2(wx, -dy);
  const alpha = Math.acos((ARM_L1 * ARM_L1 + d * d - ARM_L2 * ARM_L2) / (2 * ARM_L1 * d));
  const beta = Math.acos((ARM_L1 * ARM_L1 + ARM_L2 * ARM_L2 - d * d) / (2 * ARM_L1 * ARM_L2));
  const a1 = phi - alpha;
  const a2 = Math.PI - beta;
  return [a1, a2, c - a1 - a2];
}

function armFk([a1, a2, a3]) {
  const S = [0, -ARM_SH];
  const E = [S[0] + ARM_L1 * Math.sin(a1), S[1] - ARM_L1 * Math.cos(a1)];
  const b = a1 + a2;
  const W = [E[0] + ARM_L2 * Math.sin(b), E[1] - ARM_L2 * Math.cos(b)];
  const c = b + a3;
  const T = [W[0] + ARM_L3 * Math.sin(c), W[1] - ARM_L3 * Math.cos(c)];
  return { S, E, W, T, a1, b, c };
}

export const armTip = (pose) => armFk(pose).T;

export const ARM_POSES = {
  fold: [0.05, 2.85, 0.6],
  home: armIk(95, -160, 2.3),
  a: armIk(64, -100, Math.PI),
  aHit: armIk(64, -93, Math.PI),
  b: armIk(118, -100, Math.PI),
  bHit: armIk(118, -93, Math.PI),
  scan: armIk(112, -146, 2.65),
};
// One machine cycle as [from, to, start, end] fractions; it holds between.
const ARM_CYCLE = [
  ["home", "a", 0.0, 0.1], ["a", "aHit", 0.1, 0.14], ["aHit", "a", 0.14, 0.19],
  ["a", "b", 0.19, 0.3], ["b", "bHit", 0.3, 0.34], ["bHit", "b", 0.34, 0.39],
  ["b", "home", 0.39, 0.52], ["home", "scan", 0.7, 0.78], ["scan", "home", 0.84, 0.92],
];
export const ARM_HITS = [0.14, 0.34];
const mixJoints = (A, B, k) => A.map((v, i) => lerp(v, B[i], k));
export function armCycle(f) {
  for (const [from, to, a, b] of ARM_CYCLE) {
    if (f < a) return ARM_POSES[from];
    if (f <= b) return mixJoints(ARM_POSES[from], ARM_POSES[to], ease.inOut((f - a) / (b - a)));
  }
  return ARM_POSES.home;
}

export function Arm({ x, y, s, facing = 1, pose, lit, led, opacity = 1, hoist = 0 }) {
  const { S, E, W, T, a1, b, c } = armFk(pose);
  const body = mixColor("#0b1015", "#b4c2d0", lit);
  const dark = mixColor("#06080b", "#2c3743", lit);
  const sw = 1.3 / s;
  const rim = { stroke: C.cold, strokeOpacity: 0.12 + 0.36 * (1 - lit), strokeWidth: sw };
  const link = (A, ang, len, w, fill, extra = {}) => (
    <rect x={-w / 2} y={-len} width={w} height={len} rx={w / 2} fill={fill} {...extra}
      transform={`translate(${A[0].toFixed(2)} ${A[1].toFixed(2)}) rotate(${deg(ang).toFixed(2)})`} />
  );
  return (
    <g transform={`translate(${round1(x)} ${round1(y)}) scale(${s * facing} ${s})`} opacity={opacity}>
      {hoist > 0 && (
        <path d={`M -12 ${-ARM_SH - 16} L -12 ${-ARM_SH - 16 - hoist} M 12 ${-ARM_SH - 16} L 12 ${-ARM_SH - 16 - hoist}`}
          stroke={mixColor("#2a3540", "#8796a5", lit)} strokeWidth={1.6 / s} />
      )}
      <ellipse cx={0} cy={0} rx={46} ry={6} fill="#000" opacity={0.45 * lit} />
      <rect x={-34} y={-12} width={68} height={12} rx={2} fill={dark} />
      <rect x={-25} y={-50} width={50} height={40} rx={7} fill={body} {...rim} />
      <circle cx={S[0]} cy={S[1]} r={21} fill={dark} />
      {link(S, a1, ARM_L1, 30, body, rim)}
      {link(E, b + Math.PI, 28, 24, dark)}
      {link(E, b, ARM_L2, 20, body, rim)}
      {link(E, b, ARM_L2 * 0.62, 3, C.cold, { opacity: led * 0.85 })}
      <circle cx={E[0]} cy={E[1]} r={15} fill={dark} />
      <circle cx={E[0]} cy={E[1]} r={5} fill={body} />
      <circle cx={S[0]} cy={S[1]} r={8} fill={body} />
      <circle cx={W[0]} cy={W[1]} r={9} fill={dark} />
      {link(W, c, ARM_L3, 11, dark)}
      <circle cx={T[0]} cy={T[1]} r={3} fill={body} />
      <circle cx={13} cy={-34} r={3.4} fill={C.coldHot} opacity={led} />
    </g>
  );
}

// One width per point; zero-length segments (a curled finger) keep the last
// direction.
export function taper(pts, widths) {
  const dirs = [];
  let last = [0, 1];
  for (let i = 0; i < pts.length - 1; i++) {
    const dx = pts[i + 1][0] - pts[i][0];
    const dy = pts[i + 1][1] - pts[i][1];
    const len = Math.hypot(dx, dy);
    if (len > 0.5) last = [dx / len, dy / len];
    dirs.push(last);
  }
  const L = [];
  const R = [];
  pts.forEach((p, i) => {
    const a = dirs[Math.max(0, i - 1)];
    const b = dirs[Math.min(dirs.length - 1, i)];
    const n = Math.hypot(a[0] + b[0], a[1] + b[1]) || 1;
    const d = [(a[0] + b[0]) / n, (a[1] + b[1]) / n];
    const w = widths[i] / 2;
    L.push([p[0] - d[1] * w, p[1] + d[0] * w]);
    R.push([p[0] + d[1] * w, p[1] - d[0] * w]);
  });
  const r = widths[widths.length - 1] / 2;
  const e = R[R.length - 1];
  const back = R.slice(0, -1).reverse().map(([px, py]) => `L ${round1(px)} ${round1(py)}`).join(" ");
  return `${linePath(L)} A ${round1(r)} ${round1(r)} 0 0 0 ${round1(e[0])} ${round1(e[1])} ${back} Z`;
}
